import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaMicrosoft,
  FaRobot,
  FaComments,
  FaCalendarAlt,
  FaUsers,
  FaChartLine,
  FaFileAlt,
  FaClock,
  FaCheckCircle,
  FaSync,
  FaShieldAlt,
  FaMobile,
  FaDesktop,
  FaCloud,
  FaArrowRight,
  FaPlay,
  FaDownload,
  FaStar,
  FaRocket,
  FaCog,
  FaBell,
  FaSearch,
  FaHandshake,
  FaUserTie,
  FaTasks,
  FaQuestionCircle,
  FaExclamationTriangle,
  FaLightbulb,
  FaChevronRight,
  FaChevronLeft,
  FaRegClock,
  FaRegCheckCircle,
  FaRegComments,
  FaRegChartBar
} from 'react-icons/fa';

export default function TeamsHRAssistant() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const featuresRef = useRef(null);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 300);
      setIsScrolled(window.pageYOffset > 100);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextFeature = () => {
    setActiveFeature((prev) => (prev + 1) % features.length);
  };

  const prevFeature = () => {
    setActiveFeature((prev) => (prev - 1 + features.length) % features.length);
  };

  const features = [
    {
      icon: FaRobot,
      title: "AI-Powered HR Assistant",
      description: "Get instant answers to HR questions, policy clarifications, and employee queries directly in Teams with our intelligent chatbot that learns from your organization's specific policies.",
      color: "#6264A7",
      image: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=500&h=400&fit=crop",
      stats: "70% faster response time"
    },
    {
      icon: FaCalendarAlt,
      title: "Smart Meeting Integration",
      description: "Automatically schedule interviews, performance reviews, and team meetings with AI-powered scheduling that considers availability across time zones and priorities.",
      color: "#464775",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=500&h=400&fit=crop",
      stats: "85% reduction in scheduling conflicts"
    },
    {
      icon: FaUsers,
      title: "Employee Self-Service Portal",
      description: "Enable employees to request time off, access payslips, update personal information, and track career development through an intuitive Teams interface.",
      color: "#FF8C00",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=500&h=400&fit=crop",
      stats: "60% fewer HR tickets"
    },
    {
      icon: FaChartLine,
      title: "Real-time People Analytics",
      description: "Access comprehensive HR metrics, team performance insights, and engagement analytics through interactive dashboards that update in real-time.",
      color: "#107C10",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=400&fit=crop",
      stats: "45% better decision making"
    }
  ];

  const capabilities = [
    {
      icon: FaRegComments,
      title: "24/7 HR Support",
      description: "Round-the-clock AI assistance for HR queries with seamless escalation to human specialists when needed",
      items: ["Policy clarification", "Leave balance queries", "Benefits information", "Onboarding support", "Compliance guidance"],
      color: "#6264A7"
    },
    {
      icon: FaTasks,
      title: "Workflow Automation",
      description: "Automate complex approval processes and routine HR tasks with intelligent routing and notifications",
      items: ["Leave approvals", "Expense claims", "Document approvals", "Onboarding checklists", "Offboarding workflows"],
      color: "#464775"
    },
    {
      icon: FaUserTie,
      title: "Manager Excellence",
      description: "Specialized tools for people managers to effectively lead, support, and develop their teams",
      items: ["Performance tracking", "Team analytics", "One-on-one scheduling", "Feedback collection", "Goal setting"],
      color: "#FF8C00"
    },
    {
      icon: FaSync,
      title: "Enterprise Integration",
      description: "Deep integration with your existing HR ecosystem and business applications",
      items: ["HRIS integration", "Payroll systems", "Learning platforms", "Recruitment software", "ERP systems"],
      color: "#107C10"
    }
  ];

  const testimonials = [
    {
      name: "Jennifer Martinez",
      role: "HR Director",
      company: "GlobalTech Solutions",
      content: "HR Copilot for Teams transformed how we support our 2,500 employees. We reduced HR ticket volume by 70% while improving employee satisfaction scores.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      improvement: "70% faster response time"
    },
    {
      name: "David Kim",
      role: "IT Manager",
      company: "InnovateCorp",
      content: "The seamless integration with our existing HR stack made implementation effortless. Our adoption rate hit 95% in the first month alone.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      improvement: "95% adoption rate"
    },
    {
      name: "Sarah Johnson",
      role: "People Operations Lead",
      company: "ScaleFast Inc",
      content: "Our managers save 10+ hours weekly on administrative tasks. The automation features have been game-changing for our growing team.",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      improvement: "10+ hours saved weekly"
    }
  ];

  const integrationPartners = [
    { name: "Workday", logo: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&h=100&fit=crop", category: "HRIS", status: "Premium" },
    { name: "ADP", logo: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=100&h=100&fit=crop", category: "Payroll", status: "Enterprise" },
    { name: "BambooHR", logo: "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=100&h=100&fit=crop", category: "HRMS", status: "Standard" },
    { name: "Greenhouse", logo: "https://images.unsplash.com/photo-1611162617263-4ec3060a058e?w=100&h=100&fit=crop", category: "ATS", status: "Premium" },
    { name: "LinkedIn Talent", logo: "https://images.unsplash.com/photo-1611944212127-8e4c138b1111?w=100&h=100&fit=crop", category: "Recruiting", status: "Enterprise" },
    { name: "Slack", logo: "https://images.unsplash.com/photo-1611944212127-8e4c138b1112?w=100&h=100&fit=crop", category: "Communication", status: "Standard" },
    { name: "Salesforce", logo: "https://images.unsplash.com/photo-1611944212127-8e4c138b1113?w=100&h=100&fit=crop", category: "CRM", status: "Enterprise" },
    { name: "Oracle HCM", logo: "https://images.unsplash.com/photo-1611944212127-8e4c138b1114?w=100&h=100&fit=crop", category: "HCM", status: "Premium" }
  ];

  const stats = [
    { number: "70%", label: "Reduction in HR Tickets", icon: FaRegComments },
    { number: "15K+", label: "Organizations Served", icon: FaUsers },
    { number: "95%", label: "Employee Adoption Rate", icon: FaRegCheckCircle },
    { number: "10+", label: "Hours Saved Weekly", icon: FaRegClock }
  ];

  return (
    <>
      <style>{`
        .hrTeamsSuite {
          background: linear-gradient(135deg, #f5f7ff 0%, #f0f4ff 50%, #e8eeff 100%);
          min-height: 100vh;
          position: relative;
          overflow: hidden;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }

        .hrTeamsContainer {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 2;
        }

        /* Enhanced Animated Background */
        .hrTeamsOrbs {
          position: fixed;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          pointer-events: none;
          z-index: 1;
        }

        .hrTeamsOrb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.12;
          animation: hrTeamsFloat 12s ease-in-out infinite;
        }

        .hrTeamsOrb:nth-child(1) {
          width: 600px;
          height: 600px;
          background: linear-gradient(135deg, #6264A7, #8A8BFF);
          top: 10%;
          left: 5%;
          animation-delay: 0s;
        }

        .hrTeamsOrb:nth-child(2) {
          width: 500px;
          height: 500px;
          background: linear-gradient(135deg, #464775, #6264A7);
          top: 60%;
          right: 10%;
          animation-delay: 4s;
        }

        .hrTeamsOrb:nth-child(3) {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #FF8C00, #FFB366);
          bottom: 20%;
          left: 15%;
          animation-delay: 8s;
        }

        .hrTeamsOrb:nth-child(4) {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #107C10, #4CAF50);
          top: 30%;
          right: 20%;
          animation-delay: 6s;
        }

        @keyframes hrTeamsFloat {
          0%, 100% { 
            transform: translate(0, 0) scale(1) rotate(0deg); 
          }
          33% { 
            transform: translate(40px, -40px) scale(1.1) rotate(120deg); 
          }
          66% { 
            transform: translate(-20px, 30px) scale(0.9) rotate(240deg); 
          }
        }

        /* Enhanced Hero Section */
        .hrTeamsHero {
          text-align: center;
          padding: 160px 0 100px;
          position: relative;
          z-index: 3;
        }

        .hrTeamsHeroBadge {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, rgba(98, 100, 167, 0.15), rgba(70, 71, 117, 0.1));
          color: #6264A7;
          padding: 12px 28px;
          border-radius: 25px;
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 40px;
          border: 1px solid rgba(98, 100, 167, 0.3);
          backdrop-filter: blur(10px);
          animation: hrTeamsBadgePulse 3s ease-in-out infinite;
        }

        @keyframes hrTeamsBadgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .hrTeamsHeroTitle {
          font-size: 5rem;
          font-weight: 900;
          background: linear-gradient(135deg, #464775, #6264A7, #8A8BFF);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 30px 0;
          line-height: 1.1;
          letter-spacing: -0.02em;
        }

        .hrTeamsHeroSubtitle {
          font-size: 1.5rem;
          color: #64748B;
          margin: 0 0 50px 0;
          line-height: 1.6;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          font-weight: 400;
        }

        .hrTeamsHeroButtons {
          display: flex;
          gap: 25px;
          justify-content: center;
          margin-bottom: 80px;
          flex-wrap: wrap;
        }

        .hrTeamsButton {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          border-radius: 16px;
          font-size: 17px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          border: none;
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsButton::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.6s;
        }

        .hrTeamsButton:hover::before {
          left: 100%;
        }

        .hrTeamsButtonPrimary {
          background: linear-gradient(135deg, #6264A7, #464775);
          color: white;
          box-shadow: 0 15px 40px rgba(98, 100, 167, 0.4);
        }

        .hrTeamsButtonPrimary:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 25px 60px rgba(98, 100, 167, 0.6);
        }

        .hrTeamsButtonSecondary {
          background: rgba(255, 255, 255, 0.9);
          color: #6264A7;
          border: 2px solid #6264A7;
          backdrop-filter: blur(10px);
        }

        .hrTeamsButtonSecondary:hover {
          background: #6264A7;
          color: white;
          transform: translateY(-5px);
          box-shadow: 0 20px 50px rgba(98, 100, 167, 0.3);
        }

        /* Stats Section */
        .hrTeamsStats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 30px;
          margin: 80px 0;
        }

        .hrTeamsStatCard {
          background: rgba(255, 255, 255, 0.8);
          padding: 40px 30px;
          border-radius: 20px;
          text-align: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsStatCard::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(135deg, #6264A7, #464775);
        }

        .hrTeamsStatCard:hover {
          transform: translateY(-10px);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
        }

        .hrTeamsStatIcon {
          font-size: 48px;
          color: #6264A7;
          margin-bottom: 20px;
          opacity: 0.9;
        }

        .hrTeamsStatNumber {
          font-size: 3rem;
          font-weight: 800;
          background: linear-gradient(135deg, #464775, #6264A7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 10px 0;
          line-height: 1;
        }

        .hrTeamsStatLabel {
          color: #64748B;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        /* Enhanced Features Carousel */
        .hrTeamsFeatures {
          padding: 120px 0;
          position: relative;
        }

        .hrTeamsSectionTitle {
          text-align: center;
          font-size: 3.5rem;
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 25px 0;
          position: relative;
        }

        .hrTeamsSectionTitle::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 4px;
          background: linear-gradient(135deg, #6264A7, #464775);
          border-radius: 2px;
        }

        .hrTeamsSectionSubtitle {
          text-align: center;
          font-size: 1.3rem;
          color: #6B7280;
          margin: 0 0 80px 0;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .hrTeamsFeaturesCarousel {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
          margin-bottom: 100px;
          position: relative;
        }

        .hrTeamsFeaturesContent {
          padding-right: 40px;
        }

        .hrTeamsFeatureActive {
          background: rgba(255, 255, 255, 0.9);
          padding: 50px 40px;
          border-radius: 25px;
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.15);
          border-left: 6px solid #6264A7;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsFeatureActive::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(98, 100, 167, 0.03), rgba(70, 71, 117, 0.05));
          z-index: -1;
        }

        .hrTeamsFeatureIcon {
          font-size: 56px;
          margin-bottom: 25px;
          transition: transform 0.4s ease;
        }

        .hrTeamsFeatureActive:hover .hrTeamsFeatureIcon {
          transform: scale(1.1) rotate(5deg);
        }

        .hrTeamsFeatureTitle {
          font-size: 2.2rem;
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 20px 0;
          line-height: 1.2;
        }

        .hrTeamsFeatureDescription {
          font-size: 1.2rem;
          color: #6B7280;
          line-height: 1.7;
          margin: 0 0 25px 0;
        }

        .hrTeamsFeatureStats {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(98, 100, 167, 0.1);
          color: #6264A7;
          padding: 10px 20px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 700;
        }

        .hrTeamsFeaturesVisual {
          position: relative;
          height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          perspective: 1000px;
        }

        .hrTeamsFeatureImage {
          width: 100%;
          max-width: 500px;
          border-radius: 20px;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.25);
          transition: all 0.5s ease;
          transform-style: preserve-3d;
          animation: hrTeamsImageFloat 6s ease-in-out infinite;
        }

        @keyframes hrTeamsImageFloat {
          0%, 100% { transform: translateY(0px) rotateX(0deg) rotateY(0deg); }
          33% { transform: translateY(-20px) rotateX(5deg) rotateY(5deg); }
          66% { transform: translateY(10px) rotateX(-3deg) rotateY(-3deg); }
        }

        .hrTeamsCarouselControls {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }

        .hrTeamsCarouselButton {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          border: 2px solid #6264A7;
          background: white;
          color: #6264A7;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 18px;
        }

        .hrTeamsCarouselButton:hover {
          background: #6264A7;
          color: white;
          transform: scale(1.1);
        }

        .hrTeamsFeaturesIndicators {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-top: 40px;
        }

        .hrTeamsIndicator {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #E5E7EB;
          cursor: pointer;
          transition: all 0.4s ease;
          position: relative;
        }

        .hrTeamsIndicator.active {
          background: #6264A7;
          transform: scale(1.3);
        }

        .hrTeamsIndicator.active::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border: 2px solid #6264A7;
          border-radius: 50%;
          animation: hrTeamsIndicatorPulse 2s infinite;
        }

        @keyframes hrTeamsIndicatorPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        /* Enhanced Capabilities Grid */
        .hrTeamsCapabilities {
          padding: 120px 0;
          background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
          border-radius: 50px;
          position: relative;
          margin: 100px 0;
        }

        .hrTeamsCapabilities::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(135deg, #6264A7, #464775, #FF8C00, #107C10);
          border-radius: 50px 50px 0 0;
        }

        .hrTeamsCapabilitiesGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 40px;
        }

        .hrTeamsCapabilityCard {
          background: white;
          padding: 50px 35px;
          border-radius: 25px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          border-top: 6px solid;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsCapabilityCard::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(98, 100, 167, 0.02), rgba(70, 71, 117, 0.05));
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .hrTeamsCapabilityCard:hover {
          transform: translateY(-15px) scale(1.02);
          box-shadow: 0 35px 80px rgba(0, 0, 0, 0.2);
        }

        .hrTeamsCapabilityCard:hover::before {
          opacity: 1;
        }

        .hrTeamsCapabilityIcon {
          font-size: 54px;
          margin-bottom: 25px;
          transition: transform 0.4s ease;
        }

        .hrTeamsCapabilityCard:hover .hrTeamsCapabilityIcon {
          transform: scale(1.2) rotate(10deg);
        }

        .hrTeamsCapabilityTitle {
          font-size: 1.8rem;
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 20px 0;
          line-height: 1.3;
        }

        .hrTeamsCapabilityDescription {
          color: #6B7280;
          line-height: 1.7;
          margin: 0 0 30px 0;
          font-size: 1.1rem;
        }

        .hrTeamsCapabilityList {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .hrTeamsCapabilityItem {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          color: #4B5563;
          font-size: 16px;
          transition: transform 0.3s ease;
        }

        .hrTeamsCapabilityItem:hover {
          transform: translateX(10px);
          color: #6264A7;
        }

        .hrTeamsCapabilityItem::before {
          content: '✓';
          color: #6264A7;
          font-weight: bold;
          font-size: 14px;
          transition: transform 0.3s ease;
        }

        .hrTeamsCapabilityItem:hover::before {
          transform: scale(1.3);
        }

        /* Enhanced Integration Partners */
        .hrTeamsIntegrations {
          padding: 120px 0;
          position: relative;
        }

        .hrTeamsIntegrationsGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 35px;
          margin-top: 80px;
        }

        .hrTeamsIntegrationCard {
          background: rgba(255, 255, 255, 0.8);
          padding: 40px 30px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          position: relative;
          overflow: hidden;
        }

        .hrTeamsIntegrationCard::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(135deg, #6264A7, #464775);
          transform: scaleX(0);
          transition: transform 0.4s ease;
        }

        .hrTeamsIntegrationCard:hover {
          transform: translateY(-10px) scale(1.05);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.2);
        }

        .hrTeamsIntegrationCard:hover::before {
          transform: scaleX(1);
        }

        .hrTeamsIntegrationLogo {
          width: 90px;
          height: 90px;
          object-fit: contain;
          margin: 0 auto 20px;
          border-radius: 15px;
          background: white;
          padding: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transition: transform 0.4s ease;
        }

        .hrTeamsIntegrationCard:hover .hrTeamsIntegrationLogo {
          transform: scale(1.1) rotate(5deg);
        }

        .hrTeamsIntegrationName {
          font-weight: 700;
          color: #1F2937;
          margin: 0 0 8px 0;
          font-size: 1.2rem;
        }

        .hrTeamsIntegrationCategory {
          color: #6B7280;
          font-size: 15px;
          margin: 0 0 12px 0;
        }

        .hrTeamsIntegrationStatus {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hrTeamsIntegrationStatus.Premium {
          background: rgba(255, 193, 7, 0.15);
          color: #FF8C00;
        }

        .hrTeamsIntegrationStatus.Enterprise {
          background: rgba(98, 100, 167, 0.15);
          color: #6264A7;
        }

        .hrTeamsIntegrationStatus.Standard {
          background: rgba(16, 124, 16, 0.15);
          color: #107C10;
        }

        /* Enhanced Testimonials */
        .hrTeamsTestimonials {
          padding: 120px 0;
          background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
          border-radius: 50px;
          margin: 100px 0;
          position: relative;
        }

        .hrTeamsTestimonials::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(135deg, #6264A7, #464775);
          border-radius: 50px 50px 0 0;
        }

        .hrTeamsTestimonialsGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 40px;
        }

        .hrTeamsTestimonialCard {
          background: white;
          padding: 50px 40px;
          border-radius: 25px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsTestimonialCard::before {
          content: '"';
          position: absolute;
          top: 20px;
          right: 30px;
          font-size: 120px;
          color: rgba(98, 100, 167, 0.1);
          font-family: serif;
          line-height: 1;
        }

        .hrTeamsTestimonialCard:hover {
          transform: translateY(-10px);
          box-shadow: 0 35px 80px rgba(0, 0, 0, 0.18);
        }

        .hrTeamsTestimonialContent {
          font-size: 1.2rem;
          color: #4B5563;
          line-height: 1.7;
          margin: 0 0 40px 0;
          font-style: italic;
          position: relative;
          z-index: 2;
        }

        .hrTeamsTestimonialAuthor {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .hrTeamsTestimonialAvatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6264A7, #464775);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 24px;
          flex-shrink: 0;
          overflow: hidden;
        }

        .hrTeamsTestimonialAvatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hrTeamsTestimonialInfo {
          flex: 1;
        }

        .hrTeamsTestimonialName {
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 5px 0;
          font-size: 1.3rem;
        }

        .hrTeamsTestimonialRole {
          color: #6B7280;
          font-size: 15px;
          margin: 0 0 8px 0;
        }

        .hrTeamsTestimonialImprovement {
          display: inline-block;
          background: rgba(98, 100, 167, 0.1);
          color: #6264A7;
          padding: 6px 16px;
          border-radius: 15px;
          font-size: 14px;
          font-weight: 700;
        }

        .hrTeamsTestimonialRating {
          display: flex;
          gap: 3px;
          margin-top: 8px;
        }

        .hrTeamsStar {
          color: #F59E0B;
          font-size: 16px;
        }

        /* Enhanced CTA Section */
        .hrTeamsCTA {
          background: linear-gradient(135deg, #6264A7, #464775, #8A8BFF);
          padding: 120px 0;
          border-radius: 50px;
          text-align: center;
          color: white;
          margin: 120px 0;
          position: relative;
          overflow: hidden;
        }

        .hrTeamsCTA::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><polygon fill="rgba(255,255,255,0.05)" points="0,1000 1000,0 1000,1000"/></svg>');
          background-size: cover;
        }

        .hrTeamsCTATitle {
          font-size: 3.5rem;
          font-weight: 900;
          margin: 0 0 25px 0;
          line-height: 1.1;
          position: relative;
          z-index: 2;
        }

        .hrTeamsCTADescription {
          font-size: 1.4rem;
          opacity: 0.95;
          margin: 0 0 50px 0;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
          position: relative;
          z-index: 2;
          font-weight: 400;
        }

        .hrTeamsCTAButtons {
          display: flex;
          gap: 25px;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
          z-index: 2;
        }

        .hrTeamsCTAButton {
          background: rgba(255, 255, 255, 0.95);
          color: #6264A7;
          border: none;
          font-weight: 700;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
        }

        .hrTeamsCTAButton:hover {
          background: #464775;
          color: white;
          transform: translateY(-5px) scale(1.05);
        }

        /* Enhanced Scroll to Top */
        .hrTeamsScrollTop {
          position: fixed;
          bottom: 40px;
          right: 40px;
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #6264A7, #464775);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 22px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          box-shadow: 0 15px 35px rgba(98, 100, 167, 0.4);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          backdrop-filter: blur(10px);
        }

        .hrTeamsScrollTop:hover {
          transform: translateY(-8px) scale(1.1);
          box-shadow: 0 25px 50px rgba(98, 100, 167, 0.6);
        }

        /* Mouse Follow Effect */
        .hrTeamsMouseFollow {
          position: fixed;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(98, 100, 167, 0.08) 0%, rgba(98, 100, 167, 0) 70%);
          border-radius: 50%;
          pointer-events: none;
          z-index: 1;
          transform: translate(-50%, -50%);
          transition: all 0.1s ease;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .hrTeamsHeroTitle {
            font-size: 3.5rem;
          }

          .hrTeamsFeaturesCarousel {
            grid-template-columns: 1fr;
            gap: 60px;
          }

          .hrTeamsFeaturesContent {
            padding-right: 0;
          }
        }

        @media (max-width: 768px) {
          .hrTeamsHeroTitle {
            font-size: 2.8rem;
          }

          .hrTeamsHeroButtons {
            flex-direction: column;
            align-items: center;
          }

          .hrTeamsButton {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .hrTeamsSectionTitle {
            font-size: 2.5rem;
          }

          .hrTeamsFeaturesVisual {
            height: 400px;
          }

          .hrTeamsFeatureImage {
            max-width: 350px;
          }

          .hrTeamsTestimonialsGrid,
          .hrTeamsCapabilitiesGrid {
            grid-template-columns: 1fr;
          }

          .hrTeamsCTAButtons {
            flex-direction: column;
            align-items: center;
          }
        }

        @media (max-width: 480px) {
          .hrTeamsHeroTitle {
            font-size: 2.2rem;
          }

          .hrTeamsHeroSubtitle {
            font-size: 1.2rem;
          }

          .hrTeamsSectionTitle {
            font-size: 2rem;
          }

          .hrTeamsFeatureActive {
            padding: 30px 25px;
          }

          .hrTeamsFeatureTitle {
            font-size: 1.8rem;
          }
        }
      `}</style>

      {/* Mouse Follow Effect */}
      <div 
        className="hrTeamsMouseFollow"
        style={{
          left: `${mousePosition.x}px`,
          top: `${mousePosition.y}px`,
        }}
      />

      <div className="hrTeamsSuite">
        <div className="hrTeamsOrbs">
          <div className="hrTeamsOrb"></div>
          <div className="hrTeamsOrb"></div>
          <div className="hrTeamsOrb"></div>
          <div className="hrTeamsOrb"></div>
        </div>

        <div className="hrTeamsContainer">
          {/* Hero Section */}
          <div className="hrTeamsHero">
            <div className="hrTeamsHeroBadge">
              <FaMicrosoft />
              Microsoft Teams Premium Integration
            </div>
            <h1 className="hrTeamsHeroTitle">
              HR Copilot Suite for Teams
            </h1>
            <p className="hrTeamsHeroSubtitle">
              Transform your Microsoft Teams into a complete HR command center. 
              Empower your workforce with AI-driven HR assistance, automated workflows, 
              and real-time analytics—all within the platform they use every day.
            </p>
            <div className="hrTeamsHeroButtons">
              <button className="hrTeamsButton hrTeamsButtonPrimary">
                <FaRocket />
                Start Free Trial
              </button>
              <button className="hrTeamsButton hrTeamsButtonSecondary">
                <FaPlay />
                Watch Product Tour
              </button>
              <button className="hrTeamsButton hrTeamsButtonSecondary">
                <FaDownload />
                Download Datasheet
              </button>
            </div>

            {/* Stats Section */}
            <div className="hrTeamsStats">
              {stats.map((stat, index) => (
                <div key={index} className="hrTeamsStatCard">
                  <stat.icon className="hrTeamsStatIcon" />
                  <div className="hrTeamsStatNumber">{stat.number}</div>
                  <div className="hrTeamsStatLabel">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Features Carousel */}
          <section className="hrTeamsFeatures" ref={featuresRef}>
            <h2 className="hrTeamsSectionTitle">Intelligent HR Capabilities</h2>
            <p className="hrTeamsSectionSubtitle">
              Experience the future of HR management with AI-powered features designed specifically for Microsoft Teams
            </p>
            
            <div className="hrTeamsFeaturesCarousel">
              <div className="hrTeamsFeaturesContent">
                <div className="hrTeamsFeatureActive">
                  {(() => {
                    const activeFeatureData = features[activeFeature];
                    const FeatureIcon = activeFeatureData.icon;
                    
                    return (
                      <>
                        <FeatureIcon 
                          className="hrTeamsFeatureIcon" 
                          style={{ color: activeFeatureData.color }}
                        />
                        <h3 className="hrTeamsFeatureTitle">{activeFeatureData.title}</h3>
                        <p className="hrTeamsFeatureDescription">{activeFeatureData.description}</p>
                        <div className="hrTeamsFeatureStats">
                          <FaChartLine />
                          {activeFeatureData.stats}
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                <div className="hrTeamsCarouselControls">
                  <button className="hrTeamsCarouselButton" onClick={prevFeature}>
                    <FaChevronLeft />
                  </button>
                  <button className="hrTeamsCarouselButton" onClick={nextFeature}>
                    <FaChevronRight />
                  </button>
                </div>
                
                <div className="hrTeamsFeaturesIndicators">
                  {features.map((_, index) => (
                    <div
                      key={index}
                      className={`hrTeamsIndicator ${index === activeFeature ? 'active' : ''}`}
                      onClick={() => setActiveFeature(index)}
                    />
                  ))}
                </div>
              </div>
              
              <div className="hrTeamsFeaturesVisual">
                {features[activeFeature] && (
                  <img 
                    src={features[activeFeature].image}
                    alt={features[activeFeature].title}
                    className="hrTeamsFeatureImage"
                  />
                )}
              </div>
            </div>
          </section>

          {/* Capabilities Grid */}
          <section className="hrTeamsCapabilities">
            <h2 className="hrTeamsSectionTitle">Enterprise-Grade HR Solutions</h2>
            <p className="hrTeamsSectionSubtitle">
              Comprehensive HR management tools designed for modern organizations of all sizes
            </p>
            <div className="hrTeamsCapabilitiesGrid">
              {capabilities.map((capability, index) => (
                <div 
                  key={index} 
                  className="hrTeamsCapabilityCard"
                  style={{ borderTopColor: capability.color }}
                >
                  <capability.icon 
                    className="hrTeamsCapabilityIcon" 
                    style={{ color: capability.color }}
                  />
                  <h3 className="hrTeamsCapabilityTitle">{capability.title}</h3>
                  <p className="hrTeamsCapabilityDescription">{capability.description}</p>
                  <ul className="hrTeamsCapabilityList">
                    {capability.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="hrTeamsCapabilityItem">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Integration Partners */}
          <section className="hrTeamsIntegrations">
            <h2 className="hrTeamsSectionTitle">Seamless Ecosystem Integration</h2>
            <p className="hrTeamsSectionSubtitle">
              Connect with your favorite HR tools and business applications for a unified experience
            </p>
            <div className="hrTeamsIntegrationsGrid">
              {integrationPartners.map((partner, index) => (
                <div key={index} className="hrTeamsIntegrationCard">
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    className="hrTeamsIntegrationLogo"
                  />
                  <h4 className="hrTeamsIntegrationName">{partner.name}</h4>
                  <p className="hrTeamsIntegrationCategory">{partner.category}</p>
                  <span className={`hrTeamsIntegrationStatus ${partner.status}`}>
                    {partner.status}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="hrTeamsTestimonials">
            <h2 className="hrTeamsSectionTitle">Trusted by Industry Leaders</h2>
            <p className="hrTeamsSectionSubtitle">
              Join thousands of organizations transforming their HR operations with our Teams integration
            </p>
            <div className="hrTeamsTestimonialsGrid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="hrTeamsTestimonialCard">
                  <p className="hrTeamsTestimonialContent">"{testimonial.content}"</p>
                  <div className="hrTeamsTestimonialAuthor">
                    <div className="hrTeamsTestimonialAvatar">
                      <img src={testimonial.avatar} alt={testimonial.name} />
                    </div>
                    <div className="hrTeamsTestimonialInfo">
                      <h4 className="hrTeamsTestimonialName">{testimonial.name}</h4>
                      <p className="hrTeamsTestimonialRole">{testimonial.role} • {testimonial.company}</p>
                      <span className="hrTeamsTestimonialImprovement">
                        {testimonial.improvement}
                      </span>
                      <div className="hrTeamsTestimonialRating">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FaStar key={i} className="hrTeamsStar" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="hrTeamsCTA">
            <h2 className="hrTeamsCTATitle">Ready to Transform Your HR Experience?</h2>
            <p className="hrTeamsCTADescription">
              Join 15,000+ forward-thinking organizations using HR Copilot Suite 
              to create exceptional employee experiences within Microsoft Teams
            </p>
            <div className="hrTeamsCTAButtons">
              <button className="hrTeamsButton hrTeamsCTAButton">
                <FaRocket />
                Start 30-Day Free Trial
              </button>
              <button className="hrTeamsButton hrTeamsButtonSecondary">
                <FaHandshake />
                Request Enterprise Demo
              </button>
              <button className="hrTeamsButton hrTeamsButtonSecondary">
                <FaDownload />
                Get Pricing Guide
              </button>
            </div>
          </section>
        </div>
      </div>

      {isVisible && (
        <button 
          className="hrTeamsScrollTop"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <FaArrowRight style={{ transform: 'rotate(-90deg)' }} />
        </button>
      )}
    </>
  );
}